import { useEffect, useRef, useState } from "react";
import { auth } from "./firebase";
import useVisibleNotes from "./useVisibleNotes";
import { SchedulesCard } from "./Schedules";
import { getPodColor } from "./podColors";
import { formatTime, localDateKey, getInitials, getEventKind, timeAgo } from "./dateUtils";

// Dashboard tab (view === "stats"). Display only: events, pods, contacts and every
// action that changes data come from Calendar.js / App.js as props. The one thing loaded here is a
// read-only notes list (useVisibleNotes) for the Notes card and the What's new feed.
//
// Layout (Glow redesign, 2026-10-10): greeting, a swipeable 5-week "dots" strip,
// pod bubbles with a floating details card, and a side column with Up next, What's new and Notes.

const STRIP_DAYS = 35;
const WINDOWS = [["Morning", "09:00", "12:00"], ["Afternoon", "13:00", "17:00"], ["Evening", "18:00", "21:00"]];
const POD_SEEN_KEY = "friso.podSeen"; // { podId: dateKey } — a pod's "today" ring clears once opened that day

const toMins = (t) => { const [h, m] = t.split(":").map(Number); return h * 60 + m; };
const dayDiff = (key, todayKey) => Math.round((new Date(key + "T00:00") - new Date(todayKey + "T00:00")) / 86400000);
const shortDay = (key) => new Date(key + "T00:00").toLocaleDateString(undefined, { weekday: "short" });
const monthDay = (key) => new Date(key + "T00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" });

function readSeen() {
  try { return JSON.parse(localStorage.getItem(POD_SEEN_KEY) || "{}"); } catch { return {}; }
}

export default function Dashboard({
  allEventsForDisplay, allEventsFlat, mergedEvents, isEventVisibleToUser, isEventOwner,
  currentUserUid, currentUserEmail, pods, contacts,
  pendingInvitations = [], onAcceptInvitation, onDeclineInvitation,
  onPickSlot, onPlanWithPod, onAddEvent, onOpenEvent, onNavigate, onOpenNote,
  schedules = [], onAddSchedule, onEditSchedule, onToggleSchedule,
}) {
  const [selDay, setSelDay] = useState(0);
  const [weekIdx, setWeekIdx] = useState(0);
  const [openPodId, setOpenPodId] = useState(null);
  const [seen, setSeen] = useState(readSeen);
  const stripRef = useRef(null);
  const podsWrapRef = useRef(null);
  const podPopRef = useRef(null);

  // Read-only notes for the Notes card and the feed: owned + shared (memberEmails), like Notes.js.
  const notes = useVisibleNotes(currentUserUid, currentUserEmail);

  // Pod details card: close on outside click or Esc
  useEffect(() => {
    if (!openPodId) return;
    const onDown = (e) => {
      if (podPopRef.current?.contains(e.target) || e.target.closest?.(".dash-bubble")) return;
      setOpenPodId(null);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpenPodId(null); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [openPodId]);

  // Phones: swipe the pod card left or down to close it. It follows the finger, and closes past 70px.
  const podSwipeRef = useRef(null);
  const podSwipe = {
    onTouchStart: (e) => { const t = e.touches[0]; podSwipeRef.current = { x: t.clientX, y: t.clientY, dir: null }; },
    onTouchMove: (e) => {
      const st = podSwipeRef.current, pop = podPopRef.current;
      if (!st || !pop) return;
      const dx = e.touches[0].clientX - st.x, dy = e.touches[0].clientY - st.y;
      if (!st.dir && Math.hypot(dx, dy) > 10) st.dir = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
      if (st.dir === "x" && dx < 0) pop.style.transform = `translateX(${dx}px)`;
      else if (st.dir === "y" && dy > 0) pop.style.transform = `translateY(${dy}px)`;
      st.dx = dx; st.dy = dy;
    },
    onTouchEnd: () => {
      const st = podSwipeRef.current, pop = podPopRef.current;
      podSwipeRef.current = null;
      if (!st || !pop) return;
      if ((st.dir === "x" && st.dx < -70) || (st.dir === "y" && st.dy > 70)) setOpenPodId(null);
      else pop.style.transform = "";
    },
  };

  // Keep the pod details card by its bubble (the row scrolls sideways), and on screen without scrolling the page: below
  // the bubble when it fits, else moved up just enough to fit (over the bubble if need be); taller than the screen
  // (short windows), it scrolls inside itself.
  useEffect(() => {
    const pop = podPopRef.current, wrap = podsWrapRef.current;
    const bubble = wrap?.querySelector(`[data-pod="${openPodId}"]`);
    if (!pop || !wrap || !bubble) return;
    const wr = wrap.getBoundingClientRect(), br = bubble.getBoundingClientRect();
    const left = Math.max(0, Math.min(br.left - wr.left + br.width / 2 - pop.offsetWidth / 2, wr.width - pop.offsetWidth));
    pop.style.left = `${left}px`;
    const nav = document.querySelector(".app-bottom-nav");
    const bottomEdge = (nav && getComputedStyle(nav).display !== "none" ? nav.getBoundingClientRect().top : window.innerHeight) - 8;
    const header = document.querySelector(".app-mobile-header");
    const topEdge = (header && getComputedStyle(header).display !== "none" ? header.getBoundingClientRect().bottom : 0) + 8;
    pop.style.maxHeight = `${bottomEdge - topEdge}px`;
    const h = pop.offsetHeight;
    const top = Math.max(topEdge, Math.min(br.bottom + 6, bottomEdge - h)); // where it goes on screen
    pop.style.top = `${top - wr.top}px`;
  });

  const today = new Date();
  const todayKey = localDateKey(today);
  const nowMins = today.getHours() * 60 + today.getMinutes();
  // Schedules (repeating Busy, e.g. work hours) only count toward free time; they'd crowd everything else
  const allEvWithSchedules = allEventsForDisplay.filter(isEventVisibleToUser);
  const allEv = allEvWithSchedules.filter((ev) => !ev.schedule);
  const isOwn = (ev) => ev.ownerUid === currentUserUid || ev.ownerEmail === currentUserEmail;

  const contactFor = (email) => Object.values(contacts).find((ct) => ct.email === email);
  const nameOf = (email) => {
    const c = contactFor(email);
    return (c && (c.firstName || `${c.firstName || ""} ${c.lastName || ""}`.trim())) || email?.split("@")[0] || email;
  };
  const fullNameOf = (email) => {
    if (email === currentUserEmail && auth.currentUser?.displayName) return auth.currentUser.displayName; // you're not in your own contacts
    const c = contactFor(email);
    return (c && `${c.firstName || ""} ${c.lastName || ""}`.trim()) || email?.split("@")[0] || email;
  };

  // "Today", "Tomorrow", "Mon"
  const relDay = (key) => {
    const d = dayDiff(key, todayKey);
    return d === 0 ? "Today" : d === 1 ? "Tomorrow" : shortDay(key);
  };
  const dotColor = (ev) => {
    const kind = getEventKind(ev);
    if (ev._source === "google") return "var(--google-accent)";
    if (kind === "busy") return "var(--busy-accent)";
    if (kind === "traveling") return "var(--travel-accent)";
    const podId = (ev.sharedPods || [])[0];
    return podId ? getPodColor(podId).border : "var(--color-primary)";
  };
  const eventsOn = (key) => [...new Map((mergedEvents[key] || []).filter((ev) => !ev.schedule && isEventVisibleToUser(ev)).map((ev) => [ev.id, ev])).values()]
    .sort((a, b) => (a.startTime || "00:00").localeCompare(b.startTime || "00:00"));

  // ── Greeting ──
  const hour = today.getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const firstName = (auth.currentUser?.displayName || "").split(" ")[0] || nameOf(currentUserEmail);

  // ── Week strip: 35 days from today, a week per swipe ──
  const stripDays = Array.from({ length: STRIP_DAYS }, (_, i) => {
    const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() + i);
    const key = localDateKey(d);
    return { key, d, evs: eventsOn(key) };
  });
  const weekTitle = (() => {
    if (weekIdx === 0) return "Next 7 days";
    const a = stripDays[weekIdx * 7], b = stripDays[Math.min(weekIdx * 7 + 6, STRIP_DAYS - 1)];
    return `${monthDay(a.key)} – ${a.d.getMonth() === b.d.getMonth() ? b.d.getDate() : monthDay(b.key)}`;
  })();
  const scrollWeeks = (dir) => { const s = stripRef.current; if (s) s.scrollBy({ left: dir * s.clientWidth, behavior: "smooth" }); };
  const onStripScroll = () => {
    const s = stripRef.current;
    if (s && s.clientWidth) setWeekIdx(Math.min(4, Math.round(s.scrollLeft / s.clientWidth)));
  };
  const atStart = weekIdx === 0;
  const atEnd = weekIdx >= 4;
  const sel = stripDays[selDay];
  const hasAnyPlans = stripDays.some((d) => d.evs.length > 0); // a new account: prompt instead of "Nothing planned."

  // ── Up next: the next event from now on ──
  const nextEvent = allEv
    .filter((ev) => {
      const d = ev.startDate;
      if (!d || d < todayKey) return false;
      if (d > todayKey || !ev.startTime) return true;
      return toMins(ev.startTime) >= nowMins;
    })
    .sort((a, b) => (a.startDate + (a.startTime || "00:00")).localeCompare(b.startDate + (b.startTime || "00:00")))[0] || null;
  const nextIn = (() => {
    if (!nextEvent) return "";
    const days = dayDiff(nextEvent.startDate, todayKey);
    if (days === 0) {
      if (!nextEvent.startTime) return "Today";
      const mins = toMins(nextEvent.startTime) - nowMins;
      return mins < 60 ? `In ${Math.max(1, mins)} min` : `In ${Math.round(mins / 60)} hour${Math.round(mins / 60) === 1 ? "" : "s"}`;
    }
    return days === 1 ? "Tomorrow" : `In ${days} days`;
  })();
  const nextWhen = (() => {
    if (!nextEvent) return "";
    const day = new Date(nextEvent.startDate + "T00:00").toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
    if (!nextEvent.startTime) return `${day} · All day`;
    return `${day} · ${formatTime(nextEvent.startTime)}${nextEvent.endTime ? ` – ${formatTime(nextEvent.endTime)}` : ""}`;
  })();

  // ── Pods ──
  // Find a time: the next free windows over 14 days where nobody in the pod is blocked.
  // Minutes [start, end) each event takes up on day k; all-day = the whole day, no end time = 1 hour
  const busyOn = (evs, k) => evs.flatMap((ev) => {
    const s = ev.startDate, e = ev.endDate || s, t = ev.startTime;
    if (!s || k < s || k > e) return [];
    if (!t) return [[0, 1440]];
    const startM = k === s ? toMins(t) : 0;
    return [[startM, k === e ? (ev.endTime ? toMins(ev.endTime) : startM + 60) : 1440]];
  });
  const overlaps = (blocked, start, end) => blocked.some(([bs, be]) => bs < toMins(end) && be > toMins(start));
  const overlapsOn = (ev, k) => ev.startDate <= k && (ev.endDate || ev.startDate) >= k;

  const findTimes = (pod) => {
    const members = pod.members || [];
    const blocking = allEvWithSchedules.filter((ev) =>
      ev._source === "google" || ev.ownerUid === currentUserUid || members.includes(ev.ownerEmail) || (ev.sharedPods || []).includes(pod.id)
    );
    const slots = [];
    for (let i = 0; i < 14 && slots.length < 3; i++) {
      const k = localDateKey(new Date(today.getFullYear(), today.getMonth(), today.getDate() + i));
      const blocked = busyOn(blocking, k);
      WINDOWS.forEach(([label, start, end]) => {
        const s = toMins(start), e = toMins(end);
        if ((i === 0 && s <= nowMins) || blocked.some(([bs, be]) => bs < e && be > s)) return;
        slots.push({ k, label, start, end });
      });
    }
    return slots.slice(0, 3);
  };

  // Pod card "Who's free this week": per day and window, how many members are free. A member is busy when their own
  // plans/schedules (yours: also Google) overlap it; plans shared with the pod take everyone's time.
  const freeGrid = (pod) => {
    const members = pod.members || [];
    const sharedWithPod = allEvWithSchedules.filter((ev) => (ev.sharedPods || []).includes(pod.id) && getEventKind(ev) === "event");
    const mine = (m) => allEvWithSchedules.filter((ev) => ev.ownerEmail === m || (m === currentUserEmail && (ev._source === "google" || ev.ownerUid === currentUserUid)));
    const byMember = members.map((m) => mine(m));
    return [0, 1, 2, 3, 4, 5, 6].map((i) => {
      const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() + i);
      const k = localDateKey(d);
      const podPlans = sharedWithPod.filter((ev) => overlapsOn(ev, k));
      const memberBlocked = byMember.map((evs) => busyOn(evs, k));
      return {
        k, d,
        windows: WINDOWS.map(([label, start, end]) => {
          const past = i === 0 && toMins(end) <= nowMins;
          const plan = podPlans.find((ev) => overlaps(busyOn([ev], k), start, end));
          const busy = plan ? members : members.filter((m, j) => overlaps(memberBlocked[j], start, end));
          return { label, start, end, past, plan, busy, free: members.length - busy.length, total: members.length };
        }),
      };
    });
  };

  const podInfo = Object.values(pods).map((pod) => {
    const shared = allEv.filter((ev) => (ev.sharedPods || []).includes(pod.id) && getEventKind(ev) === "event");
    const upcoming = shared
      .filter((ev) => ev.startDate > todayKey || (ev.startDate === todayKey && (!ev.endTime || toMins(ev.endTime) > nowMins)))
      .sort((a, b) => (a.startDate + (a.startTime || "")).localeCompare(b.startDate + (b.startTime || "")));
    const next = upcoming[0] || null;
    const lastPast = shared.map((ev) => ev.startDate).filter((d) => d && d < todayKey).sort().reverse()[0];
    const weeksSince = lastPast ? Math.floor(dayDiff(todayKey, lastPast) / 7) : null;
    const hasToday = upcoming.some((ev) => ev.startDate === todayKey);
    const nextLabel = next
      ? `${next.title} · ${dayDiff(next.startDate, todayKey) === 0 && next.startTime && toMins(next.startTime) >= 17 * 60 ? "Tonight" : relDay(next.startDate)}${next.startTime ? ` ${formatTime(next.startTime)}` : ""}`
      : null;
    const status = next ? nextLabel : weeksSince >= 3 ? `Nothing in ${weeksSince} weeks` : "Nothing planned";
    // Who's away in the next 7 days (shared traveling blocks by members)
    const weekEndKey = localDateKey(new Date(today.getFullYear(), today.getMonth(), today.getDate() + 6));
    const away = allEv
      .filter((ev) => getEventKind(ev) === "traveling" && (pod.members || []).includes(ev.ownerEmail) && ev.ownerEmail !== currentUserEmail)
      .filter((ev) => ev.startDate <= weekEndKey && (ev.endDate || ev.startDate) >= todayKey)
      .map((ev) => {
        const s = ev.startDate < todayKey ? todayKey : ev.startDate, e = ev.endDate || ev.startDate;
        return `${nameOf(ev.ownerEmail)} is traveling ${s === e ? relDay(s) : `${relDay(s)}–${shortDay(e)}`}`;
      });
    return { pod, next, nextLabel, status, ring: hasToday && seen[pod.id] !== todayKey, away };
  }).sort((a, b) => {
    // Soonest next event first; pods with nothing planned go last, alphabetically
    const at = (p) => (p.next ? p.next.startDate + (p.next.startTime || "00:00") : null);
    const x = at(a), y = at(b);
    if (x !== y) return x === null ? 1 : y === null ? -1 : x < y ? -1 : 1;
    return a.pod.name.localeCompare(b.pod.name);
  });

  const openPod = (podId) => {
    setOpenPodId((cur) => (cur === podId ? null : podId));
    if (seen[podId] !== todayKey) {
      const next = { ...seen, [podId]: todayKey };
      setSeen(next);
      try { localStorage.setItem(POD_SEEN_KEY, JSON.stringify(next)); } catch {}
    }
  };
  const openInfo = podInfo.find((p) => p.pod.id === openPodId);

  // ── What's new: invites on top, then recent activity by others ──
  const since = Date.now() - 14 * 86400000;
  const podNameFor = (ids) => (ids || []).map((id) => pods[id]).filter(Boolean)[0];
  const feed = [
    ...allEventsFlat
      .filter((ev) => !isOwn(ev) && ev.createdAt >= since && isEventVisibleToUser(ev) && getEventKind(ev) !== "busy")
      .map((ev) => {
        const pod = podNameFor(ev.sharedPods);
        const kind = getEventKind(ev);
        const range = ev.endDate && ev.endDate !== ev.startDate ? `${shortDay(ev.startDate)}–${shortDay(ev.endDate)}` : relDay(ev.startDate);
        return {
          id: "e" + ev.id, at: ev.createdAt, email: ev.ownerEmail, pod,
          text: kind === "traveling"
            ? <><b>{nameOf(ev.ownerEmail)}</b> is traveling {range}</>
            : <><b>{nameOf(ev.ownerEmail)}</b> added <b>{ev.title}</b>{pod ? " to " : ""}</>,
          onClick: (el) => onOpenEvent(ev, el),
        };
      }),
    ...notes
      .filter((n) => {
        if ((n.hiddenBy || []).includes(currentUserEmail) || (n.updatedAt || n.createdAt) < since) return false;
        const edited = n.updatedAt && n.createdAt && n.updatedAt - n.createdAt > 60000;
        // Whoever acted last; notes saved before updatedBy existed only tell us the owner
        const actor = edited ? n.updatedBy : n.ownerEmail;
        return actor ? actor !== currentUserEmail : n.ownerEmail !== currentUserEmail;
      })
      .map((n) => {
        const pod = podNameFor(n.sharedPods);
        const edited = n.updatedAt && n.createdAt && n.updatedAt - n.createdAt > 60000;
        const title = <b>{n.title || "Untitled note"}</b>;
        return {
          id: "n" + n.id, at: n.updatedAt || n.createdAt, email: edited ? n.updatedBy || null : n.ownerEmail, pod,
          text: edited
            ? n.updatedBy
              ? <><b>{nameOf(n.updatedBy)}</b> updated the note {title}{pod ? " in " : ""}</>
              : <>The note {title} was updated{pod ? " in " : ""}</>
            : <><b>{nameOf(n.ownerEmail)}</b> added the note {title}{pod ? " in " : ""}</>,
          onClick: () => onOpenNote(n.id),
        };
      }),
  ].sort((a, b) => b.at - a.at).slice(0, 15); // the list scrolls past ~5

  // ── Notes card: the 3 most recently changed notes you can see ──
  const recentNotes = notes
    .filter((n) => !(n.hiddenBy || []).includes(currentUserEmail))
    .filter((n) => n.ownerUid === currentUserUid || n.ownerEmail === currentUserEmail || (n.sharedPods || []).some((id) => id in pods))
    .sort((a, b) => (b.updatedAt || b.createdAt || 0) - (a.updatedAt || a.createdAt || 0))
    .slice(0, 3);

  const PodTag = ({ pod }) => {
    if (!pod) return null;
    const c = getPodColor(pod.id);
    return <span className="dash-podtag" style={{ background: c.bg, color: c.text }}>{pod.name}</span>;
  };

  return (
    <div className="dash">
      <div className="dash-main">
        {/* Greeting */}
        <section className="dash-hello">
          <div className="dash-eyebrow">{today.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</div>
          <h1 className="dash-hello__title">{greeting}, {firstName}</h1>
        </section>

        {/* Week strip */}
        <section className="card dash-week" aria-label="Upcoming days">
          <div className="dash-card-head">
            <div className="dash-week__title">
              <h2>{weekTitle}</h2>
              <span className="dash-week__nav">
                <button type="button" aria-label="Previous week" disabled={atStart} onClick={() => scrollWeeks(-1)}>‹</button>
                <button type="button" aria-label="Next week" disabled={atEnd} onClick={() => scrollWeeks(1)}>›</button>
              </span>
            </div>
            <button type="button" className="btn btn--primary btn--sm" onClick={onAddEvent}>+ New event</button>
          </div>
          <div className="dash-strip" ref={stripRef} onScroll={onStripScroll}>
            {stripDays.map((day, i) => {
              const newMonth = i > 0 && day.d.getDate() === 1;
              const label = i === 0 ? "Today" : newMonth ? day.d.toLocaleDateString(undefined, { month: "short" }) : shortDay(day.key);
              return (
                <button
                  key={day.key}
                  type="button"
                  className={`dash-day${i === 0 ? " dash-day--today" : ""}${newMonth ? " dash-day--newmonth" : ""}${i % 7 === 0 ? " dash-day--snap" : ""}`}
                  aria-pressed={i === selDay}
                  aria-label={`${day.d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}, ${day.evs.length || "no"} plan${day.evs.length === 1 ? "" : "s"}`}
                  onClick={() => setSelDay(i)}
                >
                  <span className="dash-day__name">{label}</span>
                  <span className="dash-day__num">{day.d.getDate()}</span>
                  <span className="dash-day__dots">
                    {day.evs.slice(0, 4).map((ev) => <i key={ev.id} style={{ background: dotColor(ev) }} />)}
                  </span>
                </button>
              );
            })}
            <button type="button" className="dash-day dash-day--snap dash-day--more" onClick={() => onNavigate("calendar")}>
              <span className="dash-day__name">&nbsp;</span>
              <span className="dash-day__num">→</span>
              <span className="dash-day__more">Open calendar</span>
            </button>
          </div>
          <div className="dash-daylist">
            <div className="dash-eyebrow">{selDay === 0 ? "Today" : sel.d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</div>
            {sel.evs.length === 0 && (hasAnyPlans ? <div className="dash-muted">Nothing planned.</div> : (
              <div className="dash-prompt">
                <p>Nothing planned yet. Add a dinner, a trip or a game night, and share it with a pod.</p>
                <button type="button" className="btn btn--secondary btn--sm pg-btn" onClick={() => onAddEvent()}>Add a plan</button>
              </div>
            ))}
            {sel.evs.map((ev) => {
              const multi = ev.endDate && ev.endDate !== ev.startDate;
              const time = !ev.startTime || multi ? "All day" : `${formatTime(ev.startTime)}${ev.endTime ? ` – ${formatTime(ev.endTime)}` : ""}`;
              return (
                <button key={ev.id} type="button" className="dash-daylist__row" onClick={(e) => onOpenEvent(ev, e.currentTarget)}>
                  <span className="dash-daylist__time">{time}</span>
                  <span className="dash-daylist__title"><i style={{ background: dotColor(ev) }} />{ev.title || (getEventKind(ev) === "busy" ? "Busy" : "(No title)")}</span>
                </button>
              );
            })}
          </div>
        </section>

        {/* Pods */}
        <div className="dash-section-head"><h2>Your pods</h2></div>
        {podInfo.length === 0 ? (
          <div className="dash-empty-pods">
            <button type="button" className="dash-bubble dash-bubble--add" onClick={() => onNavigate("pods")}>
              <span className="dash-bubble__circle">+</span>
              <span className="dash-bubble__name">New pod</span>
            </button>
            <p>Pods are small groups you share plans with, like your family, roommates or book club.{" "}
              <button type="button" className="dash-link" onClick={() => onNavigate("pods")}>Make your first pod</button> and invite your people.</p>
          </div>
        ) : (
          <div className="dash-pods" ref={podsWrapRef}>
            <div className="dash-bubbles">
              {podInfo.map(({ pod, status, ring }) => {
                const c = getPodColor(pod.id);
                return (
                  <button
                    key={pod.id}
                    type="button"
                    data-pod={pod.id}
                    className={`dash-bubble${ring ? " dash-bubble--ring" : ""}`}
                    style={{ "--pod-solid": c.border }}
                    aria-expanded={openPodId === pod.id}
                    aria-label={`${pod.name}: ${status}${ring ? " (something today)" : ""}`}
                    onClick={() => openPod(pod.id)}
                  >
                    <span className="dash-bubble__circle" style={{ background: c.bg, color: "var(--color-text)" }}>{getInitials(pod.name)}</span>
                    <span className="dash-bubble__name">{pod.name}</span>
                    <span className="dash-bubble__status">{status}</span>
                  </button>
                );
              })}
              <button type="button" className="dash-bubble dash-bubble--add" onClick={() => onNavigate("pods")}>
                <span className="dash-bubble__circle">+</span>
                <span className="dash-bubble__name">New pod</span>
              </button>
            </div>

            {openInfo && (() => {
              const { pod, nextLabel, away } = openInfo;
              const c = getPodColor(pod.id);
              const members = pod.members || [];
              const slots = findTimes(pod);
              const grid = freeGrid(pod);
              return (
                <div className="dash-podcard" ref={podPopRef} role="dialog" aria-label={pod.name} {...podSwipe}>
                  <div className="dash-podcard__top">
                    <h3>{pod.name}</h3>
                    <span className="dash-faces">
                      {members.slice(0, 5).map((m) => <span key={m} className="dash-face" title={fullNameOf(m)}>{getInitials(fullNameOf(m))}</span>)}
                      {members.length > 5 && <span className="dash-face dash-face--more">+{members.length - 5}</span>}
                    </span>
                    <button type="button" className="pg-iconbtn dash-podcard__close" aria-label="Close" onClick={() => setOpenPodId(null)}>✕</button>
                  </div>
                  <div className="dash-podcard__lines">
                    <div>{nextLabel ? <>Next: {nextLabel}</> : "Nothing planned yet"}</div>
                    {away.length > 0 ? away.map((a) => <div key={a}>{a}</div>) : <div className="dash-muted">Everyone's around this week</div>}
                  </div>
                  <div className="dash-who">
                    <div className="dash-eyebrow">Who's free this week</div>
                    <div className="dash-who__grid" role="table" aria-label="Who's free this week">
                      <span role="presentation" />
                      {grid.map((day) => <span key={day.k} className="dash-who__day" role="columnheader">{day.d.toLocaleDateString(undefined, { weekday: "short" })}</span>)}
                      {WINDOWS.map(([label], w) => [
                        <span key={label} className="dash-who__label" role="rowheader">{label}</span>,
                        ...grid.map((day) => {
                          const c = day.windows[w];
                          const all = c.free === c.total;
                          const level = c.past ? "past" : all ? "all" : c.free >= c.total / 2 ? "some" : "few";
                          const text = c.past ? "" : all ? "All" : `${c.free}/${c.total}`;
                          const who = c.busy.map((m) => (m === currentUserEmail ? "You" : fullNameOf(m).split(" ")[0])).join(", ");
                          // Hover/focus tip: who's busy (or the pod plan that takes the slot)
                          const tip = c.past ? "" : c.plan ? `Pod plan: ${c.plan.title}` : all ? "Everyone's free" : `Busy: ${who}`;
                          const dayName = day.d.toLocaleDateString(undefined, { weekday: "long" });
                          return c.past ? (
                            <span key={day.k + label} className="dash-who__cell dash-who__cell--past" role="cell" aria-label={`${dayName} ${label.toLowerCase()}: past`} />
                          ) : (
                            <button key={day.k + label} type="button" className={`dash-who__cell dash-who__cell--${level}`} data-tip={tip}
                              aria-label={`${dayName} ${label.toLowerCase()}: ${tip}. Plan something`}
                              onClick={() => { setOpenPodId(null); onPickSlot({ k: day.k, label, start: c.start, end: c.end }, pod.id); }}>{text}</button>
                          );
                        }),
                      ])}
                    </div>
                    <div className="dash-hint">Hover to see who's busy. Tap any time to plan something.</div>
                  </div>
                  <div className="dash-free">
                    <div className="dash-eyebrow">Times you're all free</div>
                    {slots.length === 0 ? (
                      <div className="dash-muted">No time when everyone's free in the next 2 weeks.</div>
                    ) : (
                      <div className="dash-free__tiles">
                        {slots.map((s) => (
                          <button key={s.k + s.label} type="button" className="dash-free__tile" style={{ background: c.bg, color: c.text }}
                            onClick={() => { setOpenPodId(null); onPickSlot(s, pod.id); }}>
                            <span><b>{relDay(s.k)}</b><span>{formatTime(s.start).replace(":00", "")} – {formatTime(s.end).replace(":00", "")}</span></span>
                            <i aria-hidden="true" style={{ background: c.border }}>+</i>
                          </button>
                        ))}
                      </div>
                    )}
                    <div className="dash-hint">Tap a time to plan it · based on what members have shared</div>
                  </div>
                  <button type="button" className="btn btn--secondary dash-podcard__other" onClick={() => { setOpenPodId(null); onPlanWithPod(pod.id); }}>
                    Plan a different time
                  </button>
                </div>
              );
            })()}
          </div>
        )}

        {/* Schedules: under the pods on desktop; last on phones (CSS order) */}
        <SchedulesCard schedules={schedules} onAdd={onAddSchedule} onEdit={onEditSchedule} onTogglePause={onToggleSchedule} />
      </div>

      <aside className="dash-rail">
        {/* Up next */}
        <section className="card dash-upnext" aria-label="Up next">
          <div className="dash-eyebrow">{nextEvent ? `Up next · ${nextIn}` : "Up next"}</div>
          {nextEvent ? (
            <button type="button" className="dash-upnext__body" onClick={(e) => onOpenEvent(nextEvent, e.currentTarget)}>
              <h3>{nextEvent.title || "(No title)"}</h3>
              <div className="dash-upnext__line">{nextWhen}</div>
              {nextEvent.location && <div className="dash-upnext__line dash-muted">{nextEvent.location}</div>}
              {(nextEvent.sharedPods || []).length > 0 && (
                <div className="dash-upnext__pods">{(nextEvent.sharedPods || []).map((id) => pods[id] && <PodTag key={id} pod={pods[id]} />)}</div>
              )}
            </button>
          ) : (
            <p className="dash-muted">{hasAnyPlans ? "Nothing coming up. Enjoy the quiet." : "Your next plan will show here."}</p>
          )}
        </section>

        {/* What's new */}
        <section className="card dash-new" aria-label="What's new">
          <h2>What's new</h2>
          {pendingInvitations.map((inv) => {
            const c = getPodColor(inv.podId);
            return (
              <div key={inv.id} className="dash-invite" style={{ background: c.bg, color: c.text }}>
                <div><b>{nameOf(inv.inviterEmail)}</b> invited you to <b>{inv.podName}</b></div>
                <div className="dash-invite__actions">
                  <button type="button" className="btn btn--primary btn--sm" disabled={inv._joining} onClick={() => onAcceptInvitation(inv)}>{inv._joining ? "Joining…" : "Join"}</button>
                  <button type="button" className="btn btn--secondary btn--sm" disabled={inv._joining} onClick={() => onDeclineInvitation(inv)}>Not now</button>
                </div>
              </div>
            );
          })}
          {feed.length === 0 && pendingInvitations.length === 0 && <p className="dash-muted">When people in your pods add plans or notes, you'll see them here.</p>}
          {feed.length > 0 && (
            <ul className="dash-feed">
              {feed.map((item) => (
                <li key={item.id}>
                  <button type="button" className="dash-feed__item" onClick={(e) => item.onClick(e.currentTarget)}>
                    <span className="dash-face">{item.email ? getInitials(fullNameOf(item.email)) : "✎"}</span>
                    <span>
                      {item.text}{item.pod && <PodTag pod={item.pod} />}
                      <span className="dash-feed__when">{timeAgo(item.at)}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Notes */}
        {/* Clicking the card anywhere but a note or button opens the Notes page */}
        <section className="card dash-notes dash-notes--link" aria-label="Notes" title="Open Notes"
          onClick={(e) => { if (!e.target.closest("button")) onNavigate("notes"); }}>
          <div className="dash-card-head">
            <h2>Notes</h2>
            <button type="button" className="btn btn--secondary btn--sm" onClick={() => onOpenNote(null)}>+ New note</button>
          </div>
          {recentNotes.length === 0 && <p className="dash-muted">Keep a grocery list, packing list or gift ideas, and share it with a pod.</p>}
          {recentNotes.map((n) => {
            const pod = podNameFor(n.sharedPods);
            return (
              <button key={n.id} type="button" className="dash-note" onClick={() => onOpenNote(n.id)}>
                <span className="dash-note__top"><b>{n.title || "Untitled note"}</b><span>{timeAgo(n.updatedAt || n.createdAt)}</span></span>
                <span className="dash-note__preview">{n.description || "No text yet"}</span>
                {pod ? <PodTag pod={pod} /> : <span className="dash-podtag dash-podtag--private">Just you</span>}
              </button>
            );
          })}
        </section>
      </aside>
    </div>
  );
}
